import { useDidUpdate, useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { setActivePanel } from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { baseWhite } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { toast } from '../../notifications';
import { StrategyEntityModal } from '../strategy-entity-modal/strategy-entity-modal';
import { StrategyView } from '../strategy-view';
import { useCreateOutcomeMutation, useInitiativesQuery, useOutcomesQuery } from '../strategy.hooks';
import { StrategyEntityType } from '../strategy.type';
import { getOutcomesConfig } from './outcomes.config';

/**
 * Outcomes strategy level view
 */
export function Outcomes() {
  const { t } = useTranslation();
  const outcomesConfig = useMemo(() => getOutcomesConfig(t), [t]);
  const queryClient = useQueryClient();
  const { initiativesQuery } = useInitiativesQuery();
  const { query: outcomesQuery } = useOutcomesQuery();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const selectedOutcome = useStrategyStore((state) => state.selectedOutcome);
  const activePanel = useStrategyStore((state) => state.activePanel);

  const [opened, { open, close }] = useDisclosure(false);
  const [modalEntityType, setModalEntityType] = useState<StrategyEntityType>(StrategyEntityType.Outcome);

  useEffect(() => {
    return () => {
      setActivePanel(Panel.Filters);
    };
  }, []);

  useDidUpdate(() => {
    if (portfolio) {
      setActivePanel(Panel.Filters);
    }
  }, [portfolio]);

  const createOutcomeMutation = useCreateOutcomeMutation({
    onSuccess: (data) => {
      outcomesQuery.refetch();
      initiativesQuery.refetch();
      if (portfolio?.id) {
        queryClient.invalidateQueries({ queryKey: ['kpi-overview-data', portfolio.id] });
      }
      close();
      toast.success(`Objective "${data.name}" created successfully`);
    },
    onError: (error) => {
      toast.error(`Failed to create objective: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  const addOutcomeButton = (
    <Button
      data-bf="strategy-btn-add-outcome"
      leftSection={<Icon name="add_circle" size={16} color={baseWhite} />}
      variant="primary"
      radius="xl"
      onClick={() => {
        setModalEntityType(StrategyEntityType.Outcome);
        open();
      }}
      size="sm"
    >
      Add New
    </Button>
  );

  const updatedConfig = {
    ...outcomesConfig,
    renderers: {
      renderAddButton: () => addOutcomeButton,
    },
  };

  function handleSetShow(show: boolean): void {
    setActivePanel(show ? Panel.Filters : null);
  }

  return (
    <>
      <StrategyView
        config={updatedConfig}
        customShowSideSection={Boolean(selectedOutcome)}
        customFilterShow={activePanel === Panel.Filters}
        customSetFilterShow={handleSetShow}
      />
      <StrategyEntityModal
        mode="create"
        entityType={modalEntityType}
        opened={opened}
        onClose={close}
        onSubmit={createOutcomeMutation.mutateAsync}
        isSubmitting={createOutcomeMutation.isPending}
      />
    </>
  );
}
